import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {TsBadgedImage} from '../ts-badged-image/component';
import TsSharingMode from '../ts-sharing-mode/ts-sharing-mode.component';
import {ISharingModeActions} from '../../services/ts-sharing-service/contracts';
import './TsImageShareModes.scss';

export const ImageShareLayouts = {
    DEFAULT: 'default',
    SELF_SERVICE: 'self-service'
};

interface TsImageShareModesProps {
    // Image display props
    isActive?: boolean;
    displayBadge?: boolean;
    badgeValue?: string | number;
    width?: number | string;
    height?: number | string;
    url: string;
    layoutType?: string;
    onImageLoaded?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
    onClick?: () => void;

    // Quick-share props (optional - if sharing is enabled)
    isQuickShareEnabled?: boolean;
    sharingModeActions?: ISharingModeActions[];
    sharingData?: any;
    isResourceReady?: boolean;
    onShareClick?: (e: React.MouseEvent, data: any) => void;
    onShareActionClick?: (e: React.MouseEvent, type: string, data: any) => void;
}

@observer
export class TsImageShareModes extends TechseeBaseComponent<TsImageShareModesProps> {
    private getLayoutType(): string {
        const {layoutType} = this.props;
        return layoutType && layoutType !== ImageShareLayouts.DEFAULT ? layoutType : '';
    }

    private handleImageLoaded = (event: React.SyntheticEvent<HTMLImageElement>): void => {
        if (this.props.onImageLoaded) {
            this.props.onImageLoaded(event);
        }
    };

    private handleContainerClick = (e: React.MouseEvent): void => {
        // Check if click came from sharing mode - if so, ignore
        const target = e.target as HTMLElement;
        if (target.closest('.ts-sharing-mode')) {
            return;
        }

        if (this.props.onClick) {
            this.props.onClick();
        }
    };

    private handleShareClick = (e: React.MouseEvent, data: any): void => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        if (this.props.onShareClick) {
            this.props.onShareClick(e, data);
        }
    };

    private handleShareActionClick = (e: React.MouseEvent, type: string, data: any): void => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        if (this.props.onShareActionClick) {
            this.props.onShareActionClick(e, type, data);
        }
    };

    renderInternal(): JSX.Element {
        const {
            isActive,
            displayBadge,
            badgeValue,
            width,
            height,
            url,
            isQuickShareEnabled,
            sharingModeActions,
            sharingData,
            isResourceReady
        } = this.props;

        const layoutClass = this.getLayoutType();
        const className = `ts-image-share-modes ${layoutClass}`.trim();

        const showSharingMode =
            isQuickShareEnabled && sharingModeActions && sharingModeActions.length > 0 && sharingData;

        return (
            <div className={className} onClick={this.handleContainerClick}>
                {showSharingMode && (
                    <TsSharingMode
                        actions={sharingModeActions!}
                        displayMode='top-to-bottom-on-right'
                        data={sharingData}
                        isResourceReady={isResourceReady || false}
                        onShareClick={this.handleShareClick}
                        onShareActionClick={this.handleShareActionClick}
                    />
                )}
                <TsBadgedImage
                    isActive={isActive}
                    url={url}
                    height={height}
                    width={width}
                    displayBadge={displayBadge}
                    badgeValue={badgeValue}
                    onImageLoaded={this.handleImageLoaded}
                />
            </div>
        );
    }
}
